import type { MemberColor } from '@app/shared';

/**
 * Curated themes. Every theme defines complete light and dark palettes of
 * semantic tokens; feature code never uses raw colors. All combinations are
 * checked for WCAG AA contrast by test/theme-contrast.test.ts, so adding a
 * theme means adding an entry here and keeping that test green.
 */

export interface Palette {
  bg: string;
  surface: string;
  surface2: string;
  sunken: string;
  border: string;
  borderStrong: string;
  text: string;
  textMuted: string;
  primary: string;
  onPrimary: string;
  primarySoft: string;
  onPrimarySoft: string;
}

export interface ThemeDef {
  id: string;
  name: string;
  description: string;
  defaultAccent: AccentId;
  light: Palette;
  dark: Palette;
}

export type Mode = 'light' | 'dark';

export const THEMES: ThemeDef[] = [
  {
    id: 'meadow',
    name: 'Meadow',
    description: 'Calm greens and warm paper',
    defaultAccent: 'marigold',
    light: {
      bg: '#f5f3ee',
      surface: '#ffffff',
      surface2: '#ffffff',
      sunken: '#ece9e1',
      border: '#dcd7cb',
      borderStrong: '#8f897c',
      text: '#1c1f1b',
      textMuted: '#575c55',
      primary: '#2d6a5a',
      onPrimary: '#ffffff',
      primarySoft: '#dcefe8',
      onPrimarySoft: '#1f4d41',
    },
    dark: {
      bg: '#111513',
      surface: '#1a1f1c',
      surface2: '#222824',
      sunken: '#0c0f0d',
      border: '#303833',
      borderStrong: '#6f7a72',
      text: '#ecefeb',
      textMuted: '#a3aca5',
      primary: '#74c2a9',
      onPrimary: '#0b1f19',
      primarySoft: '#1f3a32',
      onPrimarySoft: '#bfe8da',
    },
  },
  {
    id: 'harbor',
    name: 'Harbor',
    description: 'Clear blues, crisp and bright',
    defaultAccent: 'coral',
    light: {
      bg: '#f2f5f8',
      surface: '#ffffff',
      surface2: '#ffffff',
      sunken: '#e6ebf1',
      border: '#d3dbe5',
      borderStrong: '#808b99',
      text: '#18202a',
      textMuted: '#525d6b',
      primary: '#24578f',
      onPrimary: '#ffffff',
      primarySoft: '#dce8f6',
      onPrimarySoft: '#1b416b',
    },
    dark: {
      bg: '#0f1318',
      surface: '#171c23',
      surface2: '#1f252e',
      sunken: '#0a0d11',
      border: '#2c343f',
      borderStrong: '#6c7888',
      text: '#e9eef4',
      textMuted: '#a1acba',
      primary: '#7fb3e8',
      onPrimary: '#08192b',
      primarySoft: '#1c3047',
      onPrimarySoft: '#cfe2f7',
    },
  },
  {
    id: 'sunset',
    name: 'Sunset',
    description: 'Terracotta and soft clay',
    defaultAccent: 'sky',
    light: {
      bg: '#f8f3ef',
      surface: '#ffffff',
      surface2: '#ffffff',
      sunken: '#efe7e0',
      border: '#e2d6cc',
      borderStrong: '#9a877a',
      text: '#241a16',
      textMuted: '#63554d',
      primary: '#a5452b',
      onPrimary: '#ffffff',
      primarySoft: '#f7e1d9',
      onPrimarySoft: '#7a301c',
    },
    dark: {
      bg: '#161110',
      surface: '#201918',
      surface2: '#29211f',
      sunken: '#0f0b0a',
      border: '#3a2f2c',
      borderStrong: '#7f6d66',
      text: '#f3ece8',
      textMuted: '#b5a69e',
      primary: '#f0a184',
      onPrimary: '#2b1006',
      primarySoft: '#43251b',
      onPrimarySoft: '#fadbcf',
    },
  },
  {
    id: 'lavender',
    name: 'Lavender',
    description: 'Gentle purples, evening light',
    defaultAccent: 'marigold',
    light: {
      bg: '#f5f3f8',
      surface: '#ffffff',
      surface2: '#ffffff',
      sunken: '#ebe7f1',
      border: '#dad4e5',
      borderStrong: '#8b839b',
      text: '#1f1a28',
      textMuted: '#5b5468',
      primary: '#644a9a',
      onPrimary: '#ffffff',
      primarySoft: '#e8e1f5',
      onPrimarySoft: '#4a3473',
    },
    dark: {
      bg: '#131119',
      surface: '#1c1924',
      surface2: '#25212f',
      sunken: '#0d0b12',
      border: '#342f40',
      borderStrong: '#776e88',
      text: '#eeebf4',
      textMuted: '#aba4b8',
      primary: '#b9a2ec',
      onPrimary: '#1e1236',
      primarySoft: '#2f2547',
      onPrimarySoft: '#e6dcfa',
    },
  },
  {
    id: 'graphite',
    name: 'Graphite',
    description: 'Quiet neutrals, minimal color',
    defaultAccent: 'coral',
    light: {
      bg: '#f4f4f5',
      surface: '#ffffff',
      surface2: '#ffffff',
      sunken: '#e9e9ec',
      border: '#d6d7dc',
      borderStrong: '#85878f',
      text: '#18191c',
      textMuted: '#55575f',
      primary: '#374151',
      onPrimary: '#ffffff',
      primarySoft: '#e3e6eb',
      onPrimarySoft: '#2a313d',
    },
    dark: {
      bg: '#121315',
      surface: '#1b1c1f',
      surface2: '#232528',
      sunken: '#0c0d0e',
      border: '#313338',
      borderStrong: '#71747c',
      text: '#eceef1',
      textMuted: '#a7aab1',
      primary: '#a9b4c6',
      onPrimary: '#13171d',
      primarySoft: '#2a2f38',
      onPrimarySoft: '#dde3ec',
    },
  },
  {
    id: 'berry',
    name: 'Berry',
    description: 'Warm raspberry and blush',
    defaultAccent: 'leaf',
    light: {
      bg: '#f8f2f4',
      surface: '#ffffff',
      surface2: '#ffffff',
      sunken: '#efe5e9',
      border: '#e3d3da',
      borderStrong: '#9b8590',
      text: '#261820',
      textMuted: '#64525b',
      primary: '#9b2f5b',
      onPrimary: '#ffffff',
      primarySoft: '#f5dde7',
      onPrimarySoft: '#74213f',
    },
    dark: {
      bg: '#161014',
      surface: '#20181d',
      surface2: '#292026',
      sunken: '#0f0a0d',
      border: '#3a2d34',
      borderStrong: '#806c76',
      text: '#f4ebef',
      textMuted: '#b7a4ad',
      primary: '#f09cbc',
      onPrimary: '#2d0c1a',
      primarySoft: '#45202f',
      onPrimarySoft: '#fad7e4',
    },
  },
];

export const ACCENT_IDS = ['marigold', 'coral', 'sky', 'leaf', 'orchid', 'rose'] as const;
export type AccentId = (typeof ACCENT_IDS)[number];

interface AccentPalette {
  accent: string;
  onAccent: string;
  accentSoft: string;
  onAccentSoft: string;
}

export const ACCENTS: Record<AccentId, { name: string; light: AccentPalette; dark: AccentPalette }> = {
  marigold: {
    name: 'Marigold',
    light: { accent: '#f2b84b', onAccent: '#2b1d00', accentSoft: '#fdf0d5', onAccentSoft: '#5c3d00' },
    dark: { accent: '#f5c769', onAccent: '#2b1d00', accentSoft: '#3b2d10', onAccentSoft: '#f8e1b0' },
  },
  coral: {
    name: 'Coral',
    light: { accent: '#e46a4e', onAccent: '#2a0c04', accentSoft: '#fbe3dc', onAccentSoft: '#7a2a16' },
    dark: { accent: '#f08b72', onAccent: '#2a0c04', accentSoft: '#45231b', onAccentSoft: '#fbd6cb' },
  },
  sky: {
    name: 'Sky',
    light: { accent: '#3d97e0', onAccent: '#031a2e', accentSoft: '#dbeefc', onAccentSoft: '#0f4675' },
    dark: { accent: '#6db2ec', onAccent: '#031a2e', accentSoft: '#183650', onAccentSoft: '#cfe6fa' },
  },
  leaf: {
    name: 'Leaf',
    light: { accent: '#5bb56b', onAccent: '#06200d', accentSoft: '#def3e2', onAccentSoft: '#1e5a2a' },
    dark: { accent: '#7fcb8c', onAccent: '#06200d', accentSoft: '#1b3a22', onAccentSoft: '#cdeed3' },
  },
  orchid: {
    name: 'Orchid',
    light: { accent: '#b779d6', onAccent: '#22072f', accentSoft: '#f2e3fa', onAccentSoft: '#5a2a73' },
    dark: { accent: '#cc9be4', onAccent: '#22072f', accentSoft: '#3a2447', onAccentSoft: '#efdcfa' },
  },
  rose: {
    name: 'Rose',
    light: { accent: '#e86a8c', onAccent: '#2e0613', accentSoft: '#fbe0e7', onAccentSoft: '#7c1d38' },
    dark: { accent: '#f291ad', onAccent: '#2e0613', accentSoft: '#47202b', onAccentSoft: '#fcd5e0' },
  },
};

/** Status colors are shared by all themes so their meaning never changes. */
export const STATUS: Record<Mode, Record<'success' | 'warning' | 'danger', { fg: string; soft: string; onSoft: string }> & { focus: string }> = {
  light: {
    success: { fg: '#1e7a46', soft: '#dcf2e4', onSoft: '#14532d' },
    warning: { fg: '#8a5300', soft: '#fbefd2', onSoft: '#5c3800' },
    danger: { fg: '#b3261e', soft: '#fbe1de', onSoft: '#7f1d17' },
    focus: '#1a5fb4',
  },
  dark: {
    success: { fg: '#6fd39a', soft: '#173524', onSoft: '#c7f0d6' },
    warning: { fg: '#f1c25e', soft: '#3d2e10', onSoft: '#f8e3b3' },
    danger: { fg: '#ff8f86', soft: '#46201d', onSoft: '#ffd7d3' },
    focus: '#8ab4f8',
  },
};

interface MemberPalette {
  solid: string;
  onSolid: string;
  soft: string;
  onSoft: string;
}

/** Person colors: used for avatars, calendar events and assignment chips. */
export const MEMBER_PALETTES: Record<MemberColor, { name: string; light: MemberPalette; dark: MemberPalette }> = {
  teal: {
    name: 'Teal',
    light: { solid: '#0f766e', onSolid: '#ffffff', soft: '#d5f5ef', onSoft: '#115e56' },
    dark: { solid: '#40c9b8', onSolid: '#032724', soft: '#12383a', onSoft: '#c8f3ec' },
  },
  coral: {
    name: 'Coral',
    light: { solid: '#c0392b', onSolid: '#ffffff', soft: '#fbe1dd', onSoft: '#8c2a1f' },
    dark: { solid: '#f08a7e', onSolid: '#2c0806', soft: '#46211d', onSoft: '#fbd5cf' },
  },
  violet: {
    name: 'Violet',
    light: { solid: '#6d3fc0', onSolid: '#ffffff', soft: '#ebe2fa', onSoft: '#4c2a8a' },
    dark: { solid: '#b391ec', onSolid: '#1c0d38', soft: '#30244a', onSoft: '#e5dafa' },
  },
  amber: {
    name: 'Amber',
    light: { solid: '#9a6200', onSolid: '#ffffff', soft: '#fbefd3', onSoft: '#6a4300' },
    dark: { solid: '#f0bd58', onSolid: '#2a1b00', soft: '#3d2f12', onSoft: '#f7e2b1' },
  },
  sky: {
    name: 'Sky',
    light: { solid: '#0b6aa8', onSolid: '#ffffff', soft: '#dbedfa', onSoft: '#0b4f7d' },
    dark: { solid: '#6cb7ef', onSolid: '#03192c', soft: '#173650', onSoft: '#d0e8fa' },
  },
  rose: {
    name: 'Rose',
    light: { solid: '#b8285a', onSolid: '#ffffff', soft: '#fae0e9', onSoft: '#861d42' },
    dark: { solid: '#f28eb0', onSolid: '#2d0616', soft: '#46202d', onSoft: '#fbd6e3' },
  },
  green: {
    name: 'Green',
    light: { solid: '#2e7d32', onSolid: '#ffffff', soft: '#dff2df', onSoft: '#1f5a22' },
    dark: { solid: '#7fcf83', onSolid: '#062009', soft: '#1d3a1f', onSoft: '#d0efd1' },
  },
  indigo: {
    name: 'Indigo',
    light: { solid: '#3f51b5', onSolid: '#ffffff', soft: '#e2e6f8', onSoft: '#2c3a8a' },
    dark: { solid: '#9aa8ee', onSolid: '#0b1238', soft: '#252c4f', onSoft: '#dde2fb' },
  },
  orange: {
    name: 'Orange',
    light: { solid: '#a64d08', onSolid: '#ffffff', soft: '#fde8d7', onSoft: '#7c3906' },
    dark: { solid: '#f5a35d', onSolid: '#2c1300', soft: '#45291a', onSoft: '#fddcc0' },
  },
  slate: {
    name: 'Slate',
    light: { solid: '#4b5563', onSolid: '#ffffff', soft: '#e5e8ed', onSoft: '#343c48' },
    dark: { solid: '#aab4c3', onSolid: '#141920', soft: '#2b313b', onSoft: '#e0e5ec' },
  },
};

export interface BackgroundDef {
  id: string;
  name: string;
}

/** Background treatments sit behind content; cards stay solid so text contrast never depends on them. */
export const BACKGROUNDS: BackgroundDef[] = [
  { id: 'plain', name: 'Plain' },
  { id: 'wash', name: 'Soft glow' },
  { id: 'dots', name: 'Dots' },
  { id: 'waves', name: 'Waves' },
  { id: 'confetti', name: 'Confetti' },
  { id: 'stripes', name: 'Stripes' },
];

/** Unknown or unset ids fall back to the first theme, which is the default. */
export function getTheme(id: string | null | undefined): ThemeDef {
  return THEMES.find((t) => t.id === id) ?? THEMES[0]!;
}

export function isAccentId(id: string | null | undefined): id is AccentId {
  return (ACCENT_IDS as readonly string[]).includes(id ?? '');
}

/** Resolve the full token map for a theme/mode/accent. */
export function resolveTokens(theme: ThemeDef, mode: Mode, accentId: AccentId): Record<string, string> {
  const p = theme[mode];
  const a = ACCENTS[accentId][mode];
  const s = STATUS[mode];
  const tokens: Record<string, string> = {
    '--color-bg': p.bg,
    '--color-surface': p.surface,
    '--color-surface-2': p.surface2,
    '--color-sunken': p.sunken,
    '--color-border': p.border,
    '--color-border-strong': p.borderStrong,
    '--color-text': p.text,
    '--color-text-muted': p.textMuted,
    '--color-primary': p.primary,
    '--color-on-primary': p.onPrimary,
    '--color-primary-soft': p.primarySoft,
    '--color-on-primary-soft': p.onPrimarySoft,
    '--color-accent': a.accent,
    '--color-on-accent': a.onAccent,
    '--color-accent-soft': a.accentSoft,
    '--color-on-accent-soft': a.onAccentSoft,
    '--color-focus': s.focus,
    '--color-scrim': mode === 'light' ? 'rgb(16 18 16 / 0.45)' : 'rgb(0 0 0 / 0.6)',
    '--shadow-color': mode === 'light' ? '30 28 20' : '0 0 0',
  };
  for (const k of ['success', 'warning', 'danger'] as const) {
    tokens[`--color-${k}`] = s[k].fg;
    tokens[`--color-${k}-soft`] = s[k].soft;
    tokens[`--color-on-${k}-soft`] = s[k].onSoft;
  }
  for (const [id, m] of Object.entries(MEMBER_PALETTES)) {
    const mp = m[mode];
    tokens[`--member-${id}`] = mp.solid;
    tokens[`--on-member-${id}`] = mp.onSolid;
    tokens[`--member-${id}-soft`] = mp.soft;
    tokens[`--on-member-${id}-soft`] = mp.onSoft;
  }
  return tokens;
}
